import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'react-hot-toast';
import { useThemeClasses } from '../hooks/useThemeClasses';

const API = process.env.REACT_APP_BACKEND_URL;

export const SystemSettings = () => {
  const theme = useThemeClasses();
  const [settings, setSettings] = useState({
    maintenance_mode: false,
    maintenance_message: 'We are currently performing scheduled maintenance. Please check back soon!',
    activity_logs_enabled: true,
    backup_enabled: false,
    backup_frequency: 'daily',
    auto_backup_time: '02:00',
    max_login_attempts: 5,
    session_timeout: 30,
    enable_two_factor: false,
    password_expiry_days: 90,
    require_strong_passwords: true
  });
  const [logs, setLogs] = useState([]);
  const [backups, setBackups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [creatingBackup, setCreatingBackup] = useState(false);
  const [uploadingBackup, setUploadingBackup] = useState(false);
  const [activeTab, setActiveTab] = useState('general');

  useEffect(() => {
    loadSettings();
  }, []);

  useEffect(() => {
    if (activeTab === 'logs') {
      loadLogs();
    } else if (activeTab === 'backups') {
      loadBackups();
    }
  }, [activeTab]);

  const loadSettings = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('access_token');
      const response = await axios.get(`${API}/api/admin/system/settings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSettings({ ...settings, ...response.data });
    } catch (error) {
      console.error('Failed to load system settings:', error);
      toast.error('Failed to load system settings');
    } finally {
      setLoading(false);
    }
  };

  const loadLogs = async () => {
    try {
      const token = localStorage.getItem('access_token');
      const response = await axios.get(`${API}/api/admin/system/activity-logs`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { limit: 50 }
      });
      setLogs(response.data.logs || []);
    } catch (error) {
      console.error('Failed to load activity logs:', error);
      toast.error('Failed to load activity logs');
    }
  };

  const loadBackups = async () => {
    try {
      const token = localStorage.getItem('access_token');
      const response = await axios.get(`${API}/api/admin/maintenance/backups`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBackups(response.data.data || []);
    } catch (error) {
      console.error('Failed to load backups:', error);
      toast.error('Failed to load backups');
    }
  };

  const createBackup = async () => {
    try {
      setCreatingBackup(true);
      const token = localStorage.getItem('access_token');
      const response = await axios.post(`${API}/api/admin/maintenance/backup`, 
        { type: 'manual' },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      if (response.data.success) {
        toast.success('Backup created successfully!');
        // Reload backups list
        await loadBackups();
      } else {
        toast.error('Backup creation failed');
      }
    } catch (error) {
      console.error('Failed to create backup:', error);
      toast.error('Failed to create backup');
    } finally {
      setCreatingBackup(false);
    }
  };

  const downloadBackup = async (filename) => {
    try {
      const token = localStorage.getItem('access_token');
      
      // Show loading toast
      const loadingToast = toast.loading('Downloading backup...');
      
      // Fetch the file with proper authentication
      const response = await axios.get(
        `${API}/api/admin/maintenance/backup/download/${filename}`,
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob' // Important for file download
        }
      );
      
      // Create blob URL and trigger download
      const blob = new Blob([response.data], { type: 'application/zip' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      
      // Cleanup
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success('Backup downloaded successfully!', { id: loadingToast });
    } catch (error) {
      console.error('Failed to download backup:', error);
      toast.error('Failed to download backup');
    }
  };

  const uploadAndRestoreBackup = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validate file type
    if (!file.name.endsWith('.zip')) {
      toast.error('Please upload a ZIP backup file');
      return;
    }

    // Confirmation dialog
    const confirmed = window.confirm(
      '⚠️ WARNING: This will restore the backup and overwrite all current data!\n\n' +
      'This action cannot be undone. Are you sure you want to continue?'
    );

    if (!confirmed) {
      event.target.value = ''; // Reset file input
      return;
    }

    try {
      setUploadingBackup(true);
      const token = localStorage.getItem('access_token');

      // Show loading toast
      const loadingToast = toast.loading('Uploading and restoring backup... This may take a few minutes.');

      // Create FormData
      const formData = new FormData();
      formData.append('file', file);

      // Upload and restore
      const response = await axios.post(
        `${API}/api/admin/maintenance/backup/upload`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          },
          timeout: 300000 // 5 minute timeout for large backups
        }
      );

      if (response.data.success) {
        toast.success('Backup restored successfully! Please refresh the page.', { 
          id: loadingToast,
          duration: 6000
        });
        
        // Reload backups list
        setTimeout(() => {
          loadBackups();
          // Suggest page reload
          if (window.confirm('Backup restored! Would you like to reload the page to see changes?')) {
            window.location.reload();
          }
        }, 2000);
      } else {
        toast.error('Backup restore failed', { id: loadingToast });
      }
    } catch (error) {
      console.error('Failed to restore backup:', error);
      const errorMessage = error.response?.data?.detail || error.message || 'Failed to restore backup';
      toast.error(errorMessage, { duration: 5000 });
    } finally {
      setUploadingBackup(false);
      event.target.value = ''; // Reset file input
    }
  };

  const deleteBackup = async (filename) => {
    // Confirmation dialog
    const confirmed = window.confirm(
      `Are you sure you want to delete this backup?\n\n` +
      `Filename: ${filename}\n\n` +
      `This action cannot be undone.`
    );

    if (!confirmed) return;

    try {
      const token = localStorage.getItem('access_token');
      const response = await axios.delete(
        `${API}/api/admin/maintenance/backup/${filename}`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      if (response.data.success) {
        toast.success(`Backup deleted: ${filename}`);
        // Reload backups list
        loadBackups();
      }
    } catch (error) {
      console.error('Failed to delete backup:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete backup');
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      const token = localStorage.getItem('access_token');
      await axios.put(`${API}/api/admin/system/settings`, settings, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('System settings saved successfully!');
    } catch (error) {
      console.error('Failed to save system settings:', error);
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const getLogIcon = (action) => {
    const icons = {
      'login': '🔐',
      'logout': '🚪',
      'create': '➕',
      'update': '✏️',
      'delete': '🗑️',
      'settings': '⚙️',
      'backup_created': '💾',
      'backup_downloaded': '⬇️',
      'default': '📝'
    };
    return icons[action] || icons.default;
  };

  const formatBytes = (bytes, decimals = 2) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const dm = decimals < 0 ? 0 : decimals;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i];
  };

  if (loading) return <div className="p-8">Loading system settings...</div>;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className={`text-2xl font-bold ${theme.textPrimary}`}>⚙️ System Settings</h2>
          <p className={`text-sm ${theme.textSecondary} mt-1`}>Configure system-wide settings and security options</p>
        </div>
        {activeTab !== 'logs' && (
          <button
            onClick={handleSave}
            disabled={saving}
            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400 transition-colors"
          >
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
        )}
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="flex space-x-8">
          {[
            { id: 'general', label: 'General', icon: '⚙️' },
            { id: 'security', label: 'Security', icon: '🔒' },
            { id: 'maintenance', label: 'Maintenance', icon: '🛠️' },
            { id: 'backups', label: 'Backups', icon: '💾' },
            { id: 'logs', label: 'Activity Logs', icon: '📋' }
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`pb-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                activeTab === tab.id
                  ? 'border-blue-500 text-blue-600'
                  : `border-transparent ${theme.textSecondary} hover:text-blue-600`
              }`}
            >
              {tab.icon} {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {/* General Tab */}
      {activeTab === 'general' && (
        <div className="space-y-6">
          <div className={`${theme.card} p-6 rounded-lg shadow-sm border ${theme.border}`}>
            <h3 className={`text-lg font-semibold mb-4 ${theme.textPrimary}`}>System Information</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className={`p-4 ${theme.bgSecondary} rounded-lg`}>
                <p className={`text-sm ${theme.textSecondary} mb-1`}>Platform Version</p>
                <p className={`text-lg font-semibold ${theme.textPrimary}`}>v1.0.0</p>
              </div>
              <div className={`p-4 ${theme.bgSecondary} rounded-lg`}>
                <p className={`text-sm ${theme.textSecondary} mb-1`}>Database Status</p>
                <p className="text-lg font-semibold text-green-600">● Connected</p>
              </div>
              <div className={`p-4 ${theme.bgSecondary} rounded-lg`}>
                <p className={`text-sm ${theme.textSecondary} mb-1`}>Active Users</p>
                <p className={`text-lg font-semibold ${theme.textPrimary}`}>{logs.filter(l => l.action === 'login').length || 0}</p>
              </div>
              <div className={`p-4 ${theme.bgSecondary} rounded-lg`}>
                <p className={`text-sm ${theme.textSecondary} mb-1`}>System Health</p>
                <p className="text-lg font-semibold text-green-600">✓ Healthy</p>
              </div>
            </div>
          </div>

          <div className={`${theme.card} p-6 rounded-lg shadow-sm border ${theme.border}`}>
            <h3 className={`text-lg font-semibold mb-4 ${theme.textPrimary}`}>Activity Logging</h3>
            <div className={`flex items-center justify-between p-4 ${theme.isDark ? 'bg-blue-900/30' : 'bg-blue-50'} rounded-lg`}>
              <div className="flex-1">
                <label className={`block text-sm font-medium mb-1 ${theme.textPrimary}`}>Enable Activity Logging</label>
                <p className={`text-sm ${theme.textSecondary}`}>Track admin actions and system events for audit purposes</p>
              </div>
              <label className="relative inline-flex items-center cursor-pointer ml-4">
                <input
                  type="checkbox"
                  checked={settings.activity_logs_enabled !== false}
                  onChange={(e) => setSettings({ ...settings, activity_logs_enabled: e.target.checked })}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
              </label>
            </div>
          </div>
        </div>
      )}

      {/* Security Tab */}
      {activeTab === 'security' && (
        <div className="space-y-6">
          <div className={`${theme.card} p-6 rounded-lg shadow-sm border border-gray-200`}>
            <h3 className="text-lg font-semibold mb-4">Login Security</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">Maximum Login Attempts</label>
                <input
                  type="number"
                  min="3"
                  max="10"
                  value={settings.max_login_attempts || 5}
                  onChange={(e) => setSettings({ ...settings, max_login_attempts: parseInt(e.target.value) })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
                <p className={`text-xs ${theme.textSecondary} mt-1`}>💡 Account will be temporarily locked after this many failed attempts</p>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Session Timeout (minutes)</label>
                <select
                  value={settings.session_timeout || 30}
                  onChange={(e) => setSettings({ ...settings, session_timeout: parseInt(e.target.value) })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                >
                  <option value="15">15 minutes</option>
                  <option value="30">30 minutes</option>
                  <option value="60">1 hour</option>
                  <option value="120">2 hours</option>
                  <option value="240">4 hours</option>
                </select>
                <p className={`text-xs ${theme.textSecondary} mt-1`}>💡 Users will be logged out after this period of inactivity</p>
              </div>
            </div>
          </div>

          <div className={`${theme.card} p-6 rounded-lg shadow-sm border border-gray-200`}>
            <h3 className="text-lg font-semibold mb-4">Password Policies</h3>
            <div className="space-y-4">
              <div className={`flex items-center justify-between p-4 ${theme.bgSecondary} rounded-lg`}>
                <div className="flex-1">
                  <label className="block text-sm font-medium mb-1">Require Strong Passwords</label>
                  <p className={`text-sm ${theme.textSecondary}`}>Must include uppercase, lowercase, numbers, and symbols</p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer ml-4">
                  <input
                    type="checkbox"
                    checked={settings.require_strong_passwords !== false}
                    onChange={(e) => setSettings({ ...settings, require_strong_passwords: e.target.checked })}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                </label>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Password Expiry (days)</label>
                <select
                  value={settings.password_expiry_days || 90}
                  onChange={(e) => setSettings({ ...settings, password_expiry_days: parseInt(e.target.value) })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                >
                  <option value="30">30 days</option>
                  <option value="60">60 days</option>
                  <option value="90">90 days</option>
                  <option value="180">180 days</option>
                  <option value="0">Never expire</option>
                </select>
                <p className={`text-xs ${theme.textSecondary} mt-1`}>💡 Users will be prompted to change password after this period</p>
              </div>

              <div className={`flex items-center justify-between p-4 ${theme.bgSecondary} rounded-lg`}>
                <div className="flex-1">
                  <label className="block text-sm font-medium mb-1">Two-Factor Authentication</label>
                  <p className={`text-sm ${theme.textSecondary}`}>Require 2FA for admin accounts</p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer ml-4">
                  <input
                    type="checkbox"
                    checked={settings.enable_two_factor || false}
                    onChange={(e) => setSettings({ ...settings, enable_two_factor: e.target.checked })}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                </label>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Maintenance Tab */}
      {activeTab === 'maintenance' && (
        <div className="space-y-6">
          <div className={`${theme.card} p-6 rounded-lg shadow-sm border border-gray-200`}>
            <h3 className="text-lg font-semibold mb-4">Maintenance Mode</h3>
            <div className="space-y-4">
              <div className="flex items-center justify-between p-4 bg-yellow-50 border-l-4 border-yellow-400 rounded-lg">
                <div className="flex-1">
                  <label className="block text-sm font-medium mb-1 text-yellow-800">Enable Maintenance Mode</label>
                  <p className="text-sm text-yellow-700">
                    ⚠️ Customers will see a maintenance page. Only admins can access the site.
                  </p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer ml-4">
                  <input
                    type="checkbox"
                    checked={settings.maintenance_mode || false}
                    onChange={(e) => setSettings({ ...settings, maintenance_mode: e.target.checked })}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-yellow-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-yellow-600"></div>
                </label>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Maintenance Message</label>
                <textarea
                  value={settings.maintenance_message || ''}
                  onChange={(e) => setSettings({ ...settings, maintenance_message: e.target.value })}
                  rows="4"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="We are currently performing scheduled maintenance. Please check back soon!"
                />
                <p className={`text-xs ${theme.textSecondary} mt-1`}>💡 This message will be displayed to customers during maintenance</p>
              </div>

              {settings.maintenance_mode && (
                <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-sm text-red-800 font-medium">🚨 Maintenance mode is currently ACTIVE</p>
                  <p className="text-sm text-red-700 mt-1">Customers cannot access the store. Remember to disable it when done.</p>
                </div>
              )}
            </div>
          </div>

          <div className={`${theme.card} p-6 rounded-lg shadow-sm border border-gray-200`}>
            <h3 className="text-lg font-semibold mb-4">Maintenance Preview</h3>
            <div className={`border-2 border-gray-300 rounded-lg p-8 ${theme.bgSecondary} text-center`}>
              <div className="text-6xl mb-4">🛠️</div>
              <h2 className={`text-2xl font-bold ${theme.textPrimary} mb-2`}>Under Maintenance</h2>
              <p className={`${theme.textSecondary} max-w-md mx-auto`}>
                {settings.maintenance_message || 'We are currently performing scheduled maintenance. Please check back soon!'}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Backups Tab */}
      {activeTab === 'backups' && (
        <div className="space-y-6">
          <div className={`${theme.card} p-6 rounded-lg shadow-sm border border-gray-200`}>
            <h3 className="text-lg font-semibold mb-4">Automatic Backups</h3>
            <div className="space-y-4">
              <div className="flex items-center justify-between p-4 bg-green-50 rounded-lg">
                <div className="flex-1">
                  <label className="block text-sm font-medium mb-1">Enable Automatic Backups</label>
                  <p className={`text-sm ${theme.textSecondary}`}>Regularly backup your database to prevent data loss</p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer ml-4">
                  <input
                    type="checkbox"
                    checked={settings.backup_enabled || false}
                    onChange={(e) => setSettings({ ...settings, backup_enabled: e.target.checked })}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-green-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-green-600"></div>
                </label>
              </div>

              {settings.backup_enabled && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-2">Backup Frequency</label>
                    <select
                      value={settings.backup_frequency || 'daily'}
                      onChange={(e) => setSettings({ ...settings, backup_frequency: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="hourly">Every Hour</option>
                      <option value="daily">Daily</option>
                      <option value="weekly">Weekly</option>
                      <option value="monthly">Monthly</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2">Backup Time</label>
                    <input
                      type="time"
                      value={settings.auto_backup_time || '02:00'}
                      onChange={(e) => setSettings({ ...settings, auto_backup_time: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    />
                    <p className={`text-xs ${theme.textSecondary} mt-1`}>💡 Backups will run at this time daily</p>
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className={`${theme.card} p-6 rounded-lg shadow-sm border border-gray-200`}>
            <h3 className="text-lg font-semibold mb-4">Manual Backup</h3>
            <div className="space-y-4">
              <p className={`text-sm ${theme.textSecondary}`}>
                Create an immediate backup of your database and important files (images, uploads, templates)
              </p>
              <div className="flex gap-3">
                <button
                  onClick={createBackup}
                  disabled={creatingBackup}
                  className="px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors font-medium disabled:bg-gray-400 disabled:cursor-not-allowed"
                >
                  {creatingBackup ? '⏳ Creating Backup...' : '💾 Create Backup Now'}
                </button>
                
                {/* Upload/Restore Backup */}
                <label className={`px-6 py-3 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors font-medium cursor-pointer ${uploadingBackup ? 'opacity-50 cursor-not-allowed' : ''}`}>
                  <input
                    type="file"
                    accept=".zip"
                    onChange={uploadAndRestoreBackup}
                    disabled={uploadingBackup}
                    className="hidden"
                  />
                  {uploadingBackup ? '⏳ Restoring...' : '📤 Upload & Restore Backup'}
                </label>
              </div>
              <div className={`mt-3 p-3 bg-orange-50 border border-orange-200 rounded-lg`}>
                <p className="text-sm text-orange-800">
                  ⚠️ <strong>Warning:</strong> Restoring a backup will overwrite all current data. Only upload backup files created by this system.
                </p>
              </div>
            </div>
          </div>

          <div className={`${theme.card} p-6 rounded-lg shadow-sm border border-gray-200`}>
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold">Recent Backups</h3>
              <button
                onClick={loadBackups}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm"
              >
                🔄 Refresh
              </button>
            </div>
            <div className="space-y-2">
              {backups.length === 0 ? (
                <p className={`text-sm ${theme.textSecondary} text-center py-8`}>
                  No backups created yet. Click "Create Backup Now" to create your first backup.
                </p>
              ) : (
                backups.map((backup, index) => (
                  <div key={index} className={`p-3 ${theme.bgSecondary} rounded-lg flex justify-between items-center`}>
                    <div className="flex-1">
                      <p className="font-medium">{backup.filename}</p>
                      <p className={`text-sm ${theme.textSecondary}`}>
                        Size: {backup.size_mb} MB • Created: {new Date(backup.created).toLocaleString()}
                      </p>
                      {backup.metadata && (
                        <p className={`text-xs ${theme.textSecondary} mt-1`}>
                          📦 Includes: Database + Files
                        </p>
                      )}
                    </div>
                    <div className="flex gap-2">
                      <button 
                        onClick={() => downloadBackup(backup.filename)}
                        className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm"
                      >
                        ⬇️ Download
                      </button>
                      <button 
                        onClick={() => deleteBackup(backup.filename)}
                        className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 text-sm"
                      >
                        🗑️ Delete
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      )}

      {/* Activity Logs Tab */}
      {activeTab === 'logs' && (
        <div className="space-y-6">
          <div className={`${theme.card} rounded-lg shadow-sm border border-gray-200`}>
            <div className="p-6 border-b border-gray-200 flex justify-between items-center">
              <h3 className="text-lg font-semibold">Recent Activity</h3>
              <button
                onClick={loadLogs}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm"
              >
                🔄 Refresh
              </button>
            </div>
            <div className="divide-y divide-gray-200">
              {logs.length === 0 ? (
                <div className={`p-12 text-center ${theme.textSecondary}`}>
                  <p className="text-lg mb-2">📋 No activity logs yet</p>
                  <p className="text-sm">Activity logs will appear here when actions are performed</p>
                </div>
              ) : (
                logs.slice(0, 20).map((log, index) => (
                  <div key={index} className={`p-4 hover:bg-gray-100 dark:hover:bg-gray-700`}>
                    <div className="flex items-start gap-3">
                      <span className="text-2xl">{getLogIcon(log.action)}</span>
                      <div className="flex-1">
                        <p className={`font-medium ${theme.textPrimary}`}>
                          {log.user_email || 'System'} - {log.action || 'action'}
                        </p>
                        <p className={`text-sm ${theme.textSecondary} mt-1`}>
                          {log.details && typeof log.details === 'object' 
                            ? JSON.stringify(log.details, null, 2)
                            : log.details || 'No details'}
                        </p>
                        <p className={`text-xs ${theme.textSecondary} mt-1`}>
                          {log.timestamp ? new Date(log.timestamp).toLocaleString() : 'Unknown time'}
                        </p>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      )}

      {/* Save Button at Bottom */}
      {activeTab !== 'logs' && (
        <div className="flex justify-end pt-4 border-t border-gray-200">
          <button
            onClick={handleSave}
            disabled={saving}
            className="px-8 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400 transition-colors font-medium"
          >
            {saving ? 'Saving...' : 'Save All Settings'}
          </button>
        </div>
      )}
    </div>
  );
};